export type UpgradeHandler = (database: IDBDatabase, oldVersion: number) => void;

export type Database = {
  get<T>(storeName: string, key: IDBValidKey): Promise<T | undefined>;
  getAll<T>(storeName: string): Promise<T[]>;
  put(storeName: string, value: unknown): Promise<void>;
  remove(storeName: string, key: IDBValidKey): Promise<void>;
  close(): void;
};

function toPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result));
    request.addEventListener("error", () =>
      reject(request.error ?? new Error("IndexedDB の要求が失敗しました")),
    );
  });
}

function runTransaction(
  database: IDBDatabase,
  storeName: string,
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, mode);
    transaction.addEventListener("complete", () => resolve());
    transaction.addEventListener("abort", () =>
      reject(transaction.error ?? new Error("トランザクションが中断されました")),
    );
    transaction.addEventListener("error", () =>
      reject(transaction.error ?? new Error("トランザクションが失敗しました")),
    );
    action(transaction.objectStore(storeName));
  });
}

export function openDatabase(
  name: string,
  version: number,
  upgrade: UpgradeHandler,
): Promise<Database> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(name, version);
    request.addEventListener("upgradeneeded", (event) => upgrade(request.result, event.oldVersion));
    request.addEventListener("error", () =>
      reject(request.error ?? new Error("IndexedDB を開けませんでした")),
    );
    request.addEventListener("success", () => {
      const database = request.result;
      resolve({
        get: (storeName, key) =>
          toPromise(database.transaction(storeName, "readonly").objectStore(storeName).get(key)),
        getAll: (storeName) =>
          toPromise(database.transaction(storeName, "readonly").objectStore(storeName).getAll()),
        put: (storeName, value) =>
          runTransaction(database, storeName, "readwrite", (store) => store.put(value)),
        remove: (storeName, key) =>
          runTransaction(database, storeName, "readwrite", (store) => store.delete(key)),
        close: () => database.close(),
      });
    });
  });
}
